import { useState } from 'react';
import { Link } from 'react-router-dom';
import apiClient from '../../utils/apiClient';
import Field from '../common/Field';
import ui from '../../pages/dashboard/DashboardUI.module.css';
import styles from '../onboarding/Onboarding.module.css';
import { passwordStrength, isValidPassword } from '../../utils/onboarding';

const STRENGTH_LABELS = ['', 'Weak password', 'Getting there', 'Good password', 'Strong password'];
const STRENGTH_CLASS = ['', styles.strengthWeak, styles.strengthOk, styles.strengthOk, styles.strengthStrong];

export default function RegisterForm({ onRegister, onJoinRequest }) {
  const [form, setForm] = useState({ name: '', email: '', password: '', companyName: '', isOwner: true });
  const [errors, setErrors] = useState({});
  const [agreed, setAgreed] = useState(false);
  const [domainWarning, setDomainWarning] = useState(null);
  const [joinState, setJoinState] = useState('idle');
  const [submitting, setSubmitting] = useState(false);

  const set = (name) => (e) => {
    setForm((f) => ({ ...f, [name]: e.target.value }));
    setErrors((errs) => ({ ...errs, [name]: undefined }));
  };

  const handleEmailBlur = async () => {
    if (!form.email.includes('@')) return;
    setJoinState('idle');
    try {
      const { data } = await apiClient.get(`/api/auth/check-domain?email=${encodeURIComponent(form.email)}`);
      setDomainWarning(data.exists ? data.company_name : null);
    } catch {
      // non-critical, silently ignore
    }
  };

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = 'Enter your full name.';
    if (!/^\S+@\S+\.\S+$/.test(form.email)) errs.email = 'Enter a valid work email.';
    if (!isValidPassword(form.password)) errs.password = 'Use at least 8 characters, with a number and a capital letter.';
    if (!form.companyName.trim()) errs.companyName = 'Enter your company name.';
    if (!agreed) errs.terms = 'Agree to the Terms of Service and Privacy Policy to continue.';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting || !validate()) return;
    setSubmitting(true);
    try {
      const fieldErrors = await onRegister(form);
      if (fieldErrors) setErrors(fieldErrors);
    } finally {
      setSubmitting(false);
    }
  };

  const handleJoin = async () => {
    setJoinState('sending');
    setJoinState((await onJoinRequest({ email: form.email, name: form.name })) ? 'sent' : 'failed');
  };

  const strength = passwordStrength(form.password);

  return (
    <form onSubmit={handleSubmit} noValidate>
      <Field label="Full name" error={errors.name}>
        <input name="name" value={form.name} onChange={set('name')} autoComplete="name" />
      </Field>

      <Field label="Work email" error={errors.email}>
        <input name="email" type="email" value={form.email} onChange={set('email')} onBlur={handleEmailBlur} autoComplete="email" />
      </Field>

      {domainWarning && (
        <div className={styles.infoBox} style={{ background: '#fffbeb', borderColor: '#f59e0b', color: '#92400e', marginBottom: 16 }}>
          <strong>Your company may already be on Nodel</strong>
          It looks like <strong style={{ display: 'inline' }}>{domainWarning}</strong> is already registered. If you're part of that team, ask your admin to invite you instead of creating a new account.
          <div style={{ marginTop: 8 }}>
            {joinState === 'sent' ? (
              <span>Request sent. Your admin has been emailed.</span>
            ) : (
              <button type="button" className={styles.link} onClick={handleJoin} disabled={joinState === 'sending'}>
                {joinState === 'failed' ? 'Could not send. Try again' : 'Ask to join'}
              </button>
            )}
          </div>
        </div>
      )}

      <Field
        label="Password"
        error={errors.password}
        hint={form.password ? STRENGTH_LABELS[strength] : 'At least 8 characters, with a number and a capital letter'}
      >
        <input name="password" type="password" value={form.password} onChange={set('password')} autoComplete="new-password" />
        {form.password && (
          <div className={styles.strength} aria-hidden="true">
            {[1, 2, 3, 4].map((n) => (
              <span key={n} className={`${styles.strengthBar} ${n <= strength ? STRENGTH_CLASS[strength] : ''}`} />
            ))}
          </div>
        )}
      </Field>

      <Field label="Company name" error={errors.companyName}>
        <input name="companyName" value={form.companyName} onChange={set('companyName')} autoComplete="organization" />
      </Field>

      <label className={styles.checkbox}>
        <input type="checkbox" checked={agreed} onChange={(e) => { setAgreed(e.target.checked); setErrors((x) => ({ ...x, terms: undefined })); }} />
        <span>I agree to the <a href="/terms" target="_blank" rel="noreferrer">Terms of Service</a> and <a href="/privacy" target="_blank" rel="noreferrer">Privacy Policy</a>.</span>
      </label>
      {errors.terms && <span role="alert" style={{ color: '#dc2626', fontSize: '0.78rem', display: 'block', margin: '-0.5rem 0 1rem' }}>{errors.terms}</span>}

      <button className={`${styles.primaryButton} ${ui.saveButton}`} type="submit" disabled={submitting}>
        {submitting ? 'Creating account…' : 'Create account'}
      </button>

      <div className={styles.footerText}>Already have an account? <Link to="/login">Log in</Link></div>
      <div className={styles.footerText} style={{ marginTop: '0.4rem' }}>Joining your team? Ask your admin to invite you instead.</div>
    </form>
  );
}
